/* eslint-disable no-param-reassign */
import type { StandardChartLayerSpec } from '../../../../core/layer';
import type { Spec as PieLayerSpec } from '../../../../layer/preset/pie/pie';
import * as utils from '../../../../util';
import {
  BaseSpec,
  ILayerTypeSpec,
  createDataTypeValidator,
  createEncodingValidator,
  createLayerProcessor
} from '../baseProcessor';
import type { Data } from '../../specProcessor';

export type Spec = BaseSpec;

// 扩展ILayerTypeSpec以包含__isEncodingValid属性
interface PieLayerTypeWithFields extends ILayerTypeSpec {
  __isEncodingValid?: boolean;
}

type PieLayerType = StandardChartLayerSpec & PieLayerSpec & PieLayerTypeWithFields;

// 创建饼图数据类型验证器
const validatePieDataTypes = createDataTypeValidator('pie', [
  { field: 'x', type: 'string' },
  { field: 'y', type: 'number' }
]);

// 创建饼图编码验证器
const validatePieEncoding = createEncodingValidator([{ field: 'x' }, { field: 'y' }]);

// 额外的检查函数
const additionalPieChecks = (layer: PieLayerType, data: Data) => {
  const { encoding } = layer;

  // 检查数据量是否在合适范围
  const dataIndex = layer.dataIndex || 0;
  const values = data[dataIndex]?.values || [];

  if (values.length > 10) {
    utils.logger.warn(
      'warning',
      `Pie chart is not recommended for large datasets. Current data points: ${values.length}`
    );
  }

  if (Array.isArray(encoding.y) && encoding.y.length > 1) {
    // 饼图不支持多Y轴，会自动降级为单Y轴
    utils.logger.warn(
      'warning',
      `Multiple Y axes are not supported in pie chart. Using only the first Y field: "${encoding.y[0]}"`
    );
  }

  // 检查是否有重复的分类值
  if (values.length > 0) {
    const categories = new Set(values.map(item => item[encoding.x as string]));
    if (categories.size < values.length) {
      utils.logger.warn(
        'warning',
        'Pie chart should not have duplicate categories. Deduplication will be applied.'
      );
    }
  }
};

// 创建并导出饼图处理器
export const pie = createLayerProcessor<PieLayerType>(
  'pie',
  validatePieEncoding,
  validatePieDataTypes,
  additionalPieChecks
);
